<template>
  <div class="home-page">
    <div class="container">
      <section class="home-showcase" v-if="siteHome.banners.length">
        <div class="showcase-track" :style="{ transform: `translateX(-${activeBannerIndex * 100}%)` }">
          <div class="showcase-slide" v-for="banner in siteHome.banners" :key="banner.id">
            <button class="showcase-link" @click="goBanner(banner)">
              <img :src="assetUrl(banner.image_url)" :alt="localizedBannerTitle(banner) || $t('首页轮播图')">
              <span class="showcase-caption" v-if="localizedBannerTitle(banner)">{{ localizedBannerTitle(banner) }}</span>
            </button>
          </div>
        </div>
        <button class="showcase-arrow prev" v-if="siteHome.banners.length > 1" @click="prevBanner" :aria-label="$t('上一张')">‹</button>
        <button class="showcase-arrow next" v-if="siteHome.banners.length > 1" @click="nextBanner" :aria-label="$t('下一张')">›</button>
        <div class="showcase-dots" v-if="siteHome.banners.length > 1">
          <button v-for="(banner, index) in siteHome.banners" :key="banner.id" :class="{ active: activeBannerIndex === index }" @click="activeBannerIndex = index" :aria-label="`${index + 1}`"></button>
        </div>
      </section>

      <section class="notice-bar" v-if="siteHome.announcements.length">
        <span class="notice-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 10v4h3l5 4V6l-5 4H4z"></path>
            <path d="M16 9.5a4 4 0 010 5M18.5 7a7.5 7.5 0 010 10"></path>
          </svg>
        </span>
        <div class="notice-scroll">
          <div class="notice-track">
            <div class="notice-item" v-for="(notice, index) in noticeLoopList" :key="`${notice.id}-${index}`">
              <strong>{{ noticeTitle(notice) }}</strong><span>{{ noticeContent(notice) }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="home-toolbar">
        <div>
          <h1>{{ $t('期货投资产品') }}</h1>
          <p>{{ $t('选择适合您的期货投资产品，持续获取收益') }}</p>
        </div>
        <div class="switch-tabs">
          <button :class="{ active: currentTab === 'trial' }" @click="switchTab('trial')">
            {{ $t('HL体验区') }}
          </button>
          <button :class="{ active: currentTab === 'official' }" @click="switchTab('official')">
            {{ $t('HL交易区') }}
          </button>
        </div>
      </section>

      <div v-if="loading" class="loading">{{ $t('加载中...') }}</div>
      <div v-else-if="currentList.length > 0" class="product-grid">
        <ProductCard
          v-for="item in currentList"
          :key="item.id"
          :product="item"
          :type="currentTab"
          @click="goDetail(item.id)"
          @buy="handleBuy(item)"
        />
      </div>
      <div v-else class="empty-panel">
        <div class="empty-title">{{ $t('暂无产品') }}</div>
        <div class="empty-desc">{{ $t('后台上架产品后会展示在这里。') }}</div>
      </div>
    </div>

    <div class="modal-overlay" v-if="showBuyModal" @click.self="showBuyModal = false">
      <div class="buy-modal">
        <div class="modal-head">
          <h3>{{ $t('确认购买') }}</h3>
          <button @click="showBuyModal = false" aria-label="取消" :title="$t('取消')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div class="buy-product">
          <span>{{ currentTab === 'trial' ? $t('HL体验区') : $t('HL交易区') }}</span>
          <strong>{{ localizedProductName(buyProduct) }}</strong>
        </div>

        <div class="buy-list">
          <div class="buy-row">
            <span>{{ $t('产品价格') }}</span>
            <strong>{{ formatAmount(buyProduct.price) }} USDT</strong>
          </div>
          <div class="buy-row" v-if="currentTab === 'trial'">
            <span>{{ $t('投资周期') }}</span>
            <strong>{{ buyProduct.period_days }} {{ $t('天') }}</strong>
          </div>
          <div class="buy-row">
            <span>{{ $t('日收益率') }}</span>
            <strong class="income">{{ formatRate(effectiveRate(buyProduct)) }}%</strong>
          </div>
          <div class="buy-row buy-qty" v-if="maxQuantity > 1">
            <span>{{ $t('购买数量') }}</span>
            <div class="qty-control">
              <button @click="buyQuantity = Math.max(1, buyQuantity - 1)">-</button>
              <input type="number" v-model.number="buyQuantity" min="1" :max="maxQuantity" />
              <button @click="buyQuantity = Math.min(maxQuantity, buyQuantity + 1)">+</button>
            </div>
          </div>
        </div>

        <div class="pay-summary">
          <div>
            <span>{{ $t('支付金额') }}</span>
            <strong>{{ formatAmount(totalAmount) }} USDT</strong>
          </div>
          <div>
            <span>{{ $t('当前余额') }}</span>
            <strong :class="{ danger: balance < totalAmount }">{{ formatAmount(balance) }} USDT</strong>
          </div>
        </div>

        <div class="buy-insufficient" v-if="balance < totalAmount">{{ $t('余额不足，请先充值') }}</div>

        <div class="modal-actions">
          <button class="secondary-action" @click="showBuyModal = false">{{ $t('取消') }}</button>
          <button class="primary-action" @click="confirmBuy" :disabled="buying">
            {{ buying ? $t('购买中...') : $t('确认购买') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductCard from '@/components/ProductCard.vue'
import { getTrialProducts, buyTrialProduct, getOfficialProducts, buyOfficialProduct, getSiteHome } from '@/api'
import { formatAmount } from '@/utils/web3'

export default {
  name: 'HomePage',
  components: { ProductCard },
  data() {
    return {
      currentTab: 'trial',
      trialList: [],
      officialList: [],
      loading: false,
      siteHome: { banners: [], announcements: [] },
      activeBannerIndex: 0,
      bannerTimer: null,
      showBuyModal: false,
      buyProduct: {},
      buyQuantity: 1,
      buying: false
    }
  },
  computed: {
    currentList() { return this.currentTab === 'trial' ? this.trialList : this.officialList },
    noticeLoopList() {
      const list = this.siteHome.announcements || []
      return list.length > 1 ? [...list, ...list] : list
    },
    balance() { return this.$store.getters.balance || 0 },
    isLoggedIn() { return this.$store.getters.isLoggedIn },
    maxQuantity() {
      if (!this.buyProduct.id) return 1
      const purchaseLimit = this.buyProduct.purchase_limit || 0
      const userCount = this.buyProduct.user_purchase_count || 0
      const stock = this.buyProduct.total_stock > 0 ? (this.buyProduct.total_stock - this.buyProduct.sold_count) : 999
      let max = stock
      if (purchaseLimit > 0) max = Math.min(max, purchaseLimit - userCount)
      return Math.max(1, max)
    },
    totalAmount() { return Number(this.buyProduct.price || 0) * this.buyQuantity }
  },
  created() { this.loadProducts(); this.loadSiteHome() },
  mounted() {
    this.bannerTimer = window.setInterval(() => {
      if (this.siteHome.banners.length > 1) this.nextBanner()
    }, 5000)
  },
  beforeDestroy() {
    if (this.bannerTimer) window.clearInterval(this.bannerTimer)
  },
  methods: {
    formatAmount,
    formatRate(val) {
      return Number(val || 0).toLocaleString('en-US', { maximumFractionDigits: 6 })
    },
    noticeTitle(notice) {
      return this.$store.state.locale === 'en-US' ? (notice.title_en || notice.title) : notice.title
    },
    noticeContent(notice) {
      return this.$store.state.locale === 'en-US' ? (notice.content_en || notice.content) : notice.content
    },
    localizedBannerTitle(banner) {
      if (!banner) return ''
      return this.$store.state.locale === 'en-US' ? (banner.title_en || banner.title) : banner.title
    },
    localizedProductName(product) {
      if (!product) return this.$t('产品')
      const value = this.$store.state.locale === 'en-US'
        ? (product.name_en || product.name)
        : product.name
      return value || this.$t('产品')
    },
    effectiveRate(product) {
      if (this.currentTab === 'official') {
        const previewRate = Number(this.$store.state.user?.official_earning_preview?.daily_rate || 0)
        if (previewRate > 0) return previewRate
      }
      return Number(product?.daily_rate || 0)
    },
    switchTab(tab) { this.currentTab = tab },
    assetUrl(url) {
      if (!url) return ''
      if (/^(https?:\/\/|data:)/.test(url)) return url
      return url.startsWith('/uploads') ? url : `/uploads/${url.replace(/^\/+/, '')}`
    },
    async loadSiteHome() {
      try {
        const res = await getSiteHome()
        if (res.code === 0 && res.data) this.siteHome = { banners: res.data.banners || [], announcements: res.data.announcements || [] }
      } catch (e) {}
    },
    nextBanner() {
      const total = this.siteHome.banners.length
      if (total) this.activeBannerIndex = (this.activeBannerIndex + 1) % total
    },
    prevBanner() {
      const total = this.siteHome.banners.length
      if (total) this.activeBannerIndex = (this.activeBannerIndex - 1 + total) % total
    },
    goBanner(banner) {
      if (!banner.link_url) return
      if (banner.link_url.startsWith('/#/')) return window.location.hash = banner.link_url.slice(1)
      if (banner.link_url.startsWith('#/')) return window.location.hash = banner.link_url
      window.open(banner.link_url, '_blank', 'noopener,noreferrer')
    },
    async loadProducts() {
      this.loading = true
      try {
        const [trialRes, officialRes] = await Promise.all([getTrialProducts(), getOfficialProducts()])
        if (trialRes.code === 0) this.trialList = trialRes.data
        if (officialRes.code === 0) this.officialList = officialRes.data
      } catch (e) {
        console.error('加载产品失败:', e)
      }
      this.loading = false
    },
    goDetail(id) {
      const route = this.currentTab === 'trial' ? 'TrialDetail' : 'OfficialDetail'
      this.$router.push({ name: route, params: { id } })
    },
    handleBuy(product) {
      this.goDetail(product.id)
      return
      if (!this.isLoggedIn) return this.$toast('请先连接钱包登录', 'error')
      this.buyProduct = product
      this.buyQuantity = 1
      this.showBuyModal = true
    },
    async confirmBuy() {
      if (!this.isLoggedIn) {
        this.showBuyModal = false
        return this.$toast('请先登录', 'error')
      }
      if (this.balance < this.totalAmount) return this.$toast('余额不足，请先充值', 'error')
      this.buying = true
      try {
        const fn = this.currentTab === 'trial' ? buyTrialProduct : buyOfficialProduct
        const res = await fn({ product_id: this.buyProduct.id, quantity: this.buyQuantity })
        if (res.code === 0) {
          this.$toast('购买成功', 'success')
          this.showBuyModal = false
          const p = await import('@/api').then(m => m.getUserProfile())
          if (p.code === 0) this.$store.commit('SET_USER', p.data)
          this.loadProducts()
        } else {
          this.$toast(res.message, 'error')
        }
      } catch (e) {
        this.$toast('购买失败', 'error')
      }
      this.buying = false
    }
  }
}
</script>

<style scoped>
.home-page { padding: 24px 0 56px; }
.home-showcase {
  position: relative;
  overflow: hidden;
  margin-bottom: 14px;
  border-radius: 14px;
  background: #101512;
  box-shadow: 0 14px 30px rgba(10, 18, 13, 0.14);
  aspect-ratio: 3 / 1;
}
.showcase-track { display: flex; height: 100%; transition: transform .45s ease; }
.showcase-slide { flex: 0 0 100%; min-width: 100%; height: 100%; }
.showcase-link { display: block; position: relative; width: 100%; height: 100%; padding: 0; border: 0; background: transparent; cursor: pointer; }
.showcase-link img { display: block; width: 100%; height: 100%; object-fit: cover; }
.showcase-caption { position: absolute; left: 22px; bottom: 18px; padding: 7px 12px; border-radius: 6px; background: rgba(9, 15, 11, .72); color: #d9ffdf; font-size: 14px; font-weight: 850; }
.showcase-arrow { position: absolute; top: 50%; width: 34px; height: 34px; margin-top: -17px; border: 1px solid rgba(217, 255, 223, .45); border-radius: 50%; background: rgba(9, 15, 11, .45); color: #fff; font-size: 28px; line-height: 25px; cursor: pointer; }
.showcase-arrow.prev { left: 14px; }
.showcase-arrow.next { right: 14px; }
.showcase-dots { position: absolute; left: 50%; bottom: 13px; display: flex; gap: 6px; transform: translateX(-50%); }
.showcase-dots button { width: 18px; height: 4px; padding: 0; border: 0; border-radius: 4px; background: rgba(255,255,255,.52); cursor: pointer; }
.showcase-dots button.active { background: #b7f7c2; }
.notice-bar { display: flex; align-items: center; gap: 10px; min-height: 44px; margin-bottom: 24px; padding: 0 14px; overflow: hidden; border: 1px solid #d8f0dc; border-radius: 8px; background: #f1fff3; color: #1c3923; }
.notice-icon { display: grid; flex: 0 0 22px; place-items: center; width: 22px; height: 22px; border-radius: 50%; background: #101512; color: #c7ffd0; }
.notice-icon svg { width: 15px; height: 15px; }
.notice-scroll { min-width: 0; overflow: hidden; }
.notice-track { display: flex; width: max-content; animation: notice-marquee 24s linear infinite; }
.notice-scroll:hover .notice-track { animation-play-state: paused; }
.notice-item { display: flex; gap: 10px; padding-right: 72px; white-space: nowrap; }
.notice-item strong { color: #101512; }
.notice-item span { color: #54705a; }
@keyframes notice-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.home-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}
.home-toolbar h1 {
  font-size: 26px;
  line-height: 1.2;
  color: var(--tech-text);
  font-weight: 950;
  margin-bottom: 6px;
}
.home-toolbar p {
  color: #64748b;
  font-size: 14px;
}
.switch-tabs {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: 8px;
  background: #e8eef7;
  border: 1px solid #dbe4ef;
}
.switch-tabs button {
  min-width: 96px;
  height: 38px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 850;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}
.switch-tabs button.active {
  background: #fff;
  color: #2563eb;
  box-shadow: 0 5px 14px rgba(15, 23, 42, 0.08);
}
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(288px, 1fr));
  gap: 18px;
}
.empty-panel {
  padding: 56px 20px;
  text-align: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.empty-title { color: var(--tech-text); font-size: 16px; font-weight: 900; margin-bottom: 6px; }
.empty-desc { color: #64748b; font-size: 13px; }
.buy-modal {
  width: 92%;
  max-width: 430px;
  background: #fff;
  border-radius: 8px;
  padding: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.modal-head h3 {
  font-size: 18px;
  color: var(--tech-text);
  font-weight: 950;
}
.modal-head button {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.buy-product {
  padding: 16px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f8fafc, #eef6ff);
  border: 1px solid #e7eef8;
  margin-bottom: 12px;
}
.buy-product span {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  margin-bottom: 4px;
}
.buy-product strong {
  display: block;
  color: var(--tech-text);
  font-size: 16px;
  font-weight: 950;
  overflow-wrap: anywhere;
}
.buy-list {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
}
.buy-row {
  min-height: 46px;
  padding: 10px 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #edf2f7;
}
.buy-row:last-child { border-bottom: none; }
.buy-row span { color: #64748b; font-size: 13px; font-weight: 750; }
.buy-row strong { color: var(--tech-text); font-size: 14px; font-weight: 900; text-align: right; }
.buy-row strong.income { color: #059669; }
.qty-control {
  display: flex;
  align-items: center;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  overflow: hidden;
}
.qty-control button {
  width: 34px;
  height: 34px;
  border: none;
  background: #f8fafc;
  color: #2563eb;
  font-size: 18px;
  font-weight: 900;
  cursor: pointer;
}
.qty-control input {
  width: 48px;
  height: 34px;
  border: none;
  border-left: 1px solid #dbe4ef;
  border-right: 1px solid #dbe4ef;
  text-align: center;
  outline: none;
  color: var(--tech-text);
  font-weight: 850;
  -moz-appearance: textfield;
}
.qty-control input::-webkit-outer-spin-button,
.qty-control input::-webkit-inner-spin-button { -webkit-appearance: none; }
.pay-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}
.pay-summary div {
  padding: 13px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
}
.pay-summary span {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  margin-bottom: 4px;
}
.pay-summary strong {
  display: block;
  color: #2563eb;
  font-size: 16px;
  font-weight: 950;
}
.pay-summary strong.danger { color: #dc2626; }
.buy-insufficient {
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 12px;
  text-align: center;
}
.modal-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.secondary-action,
.primary-action {
  height: 44px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
}
.secondary-action { background: #f1f5f9; color: #334155; }
.primary-action {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.18);
}
.primary-action:disabled { opacity: 0.58; cursor: not-allowed; box-shadow: none; }
@media (max-width: 640px) {
  .home-page { padding-top: 18px; }
  .home-showcase { aspect-ratio: 1.8 / 1; border-radius: 10px; }
  .showcase-caption { left: 12px; bottom: 12px; font-size: 12px; }
  .home-toolbar { display: block; }
  .switch-tabs { margin-top: 14px; width: 100%; }
  .switch-tabs button { min-width: 0; }
  .product-grid { grid-template-columns: 1fr; }
}
</style>
